import { useNavigate } from 'react-router';
import styled from '@emotion/styled';
import { GETMymeDetails } from '@myme/api';
import { FeedMymeProps, LibraryMymeProps } from '@myme/utils';
import { mq } from 'src/themes';
import { useAuth, useMediaQuery } from 'src/hooks';
import { mymeDetailsPageRoute } from 'src/routes/route-list';
import { transientOptions } from 'src/utils';
import { FlexColumn } from 'src/components/layout';
import { EmbedMymeDialog } from 'src/components/logic';
import { ReportMymeDialog } from 'src/components/logic/mymePreview/ReportMymeDialog';
import { useDialog } from 'src/components/organisms';
import { useMymePreviewOptions } from './useMymePreviewOptions';
import { MymeBottomPanelContainer } from './bottomPanel/Container';
import { MymeLinearProgress } from './bottomPanel/MymeLinearProgress';
import { MymePreviewHeader } from './header/Container';
import { MymePreviewDetails } from './main/Details';
import { MymePreviewDetailsMobile } from './main/DetailsMobile';

interface FeedProps {
  displayType: 'FEED';
  myme: FeedMymeProps;
}

interface LibraryProps {
  displayType: 'LIBRARY';
  myme: LibraryMymeProps;
}

interface DetailsProps {
  displayType: 'DETAILS';
  myme: GETMymeDetails;
}
interface DetailsMobileProps {
  displayType: 'DETAILS-MOBILE';
  myme: GETMymeDetails;
}

type Props = FeedProps | LibraryProps | DetailsProps | DetailsMobileProps;

interface StyledContainerProps {
  $previewImage: string;
  $isClickable: boolean;
  size: 'default' | 'large';
}

const StyledMymePreviewContainer = styled(
  'div',
  transientOptions
)<StyledContainerProps>(
  ({ $previewImage, $isClickable, size }) => `
  position: relative;
  height: ${size === 'default' ? '19rem' : '25rem'};
  width: ${size === 'default' ? '19rem' : '25rem'};
  border-radius: 0.75rem;
  background: url(${$previewImage});
  background-size: cover;
  cursor: ${$isClickable ? 'pointer' : 'initial'};

  display: grid;
  grid-template-areas:  'header'
                        'details'
                        'bottomPanel';
  grid-template-rows: min-content 1fr min-content;
  grid-template-columns: 100%;
  padding: 0.5rem 1rem;

  ${mq.xs}{
    height: 17rem;
    width: 17rem;
  }

  &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background: transparent linear-gradient(180deg, #00000000 0%, #00000000 20%, #000000C8 84%, #000000E6 100%)
      0% 0% no-repeat padding-box;
      border-radius: 0.72rem;
  }

  &:hover::after {
    background: ${
      size === 'default'
        ? `transparent linear-gradient(180deg, #00000000 0%, #00000000 20%,
           #000000C8 40%, #000000E6 100%) 0% 0% no-repeat padding-box`
        : null
    };
  }
`
);

export const MymePreviewContainer = (props: Props) => {
  const navigate = useNavigate();
  const reportMymeDialog = useDialog();
  const embedMymeDialog = useDialog();
  const { libraryOptions, detailsOptions } = useMymePreviewOptions(props.myme.id, {
    initializeReportFn: () => reportMymeDialog.openDialog(),
    openEmbedDialog: embedMymeDialog.openDialog
  });
  const matchesMobile = useMediaQuery('sm');
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  switch (props.displayType) {
    case 'FEED': {
      return (
        <StyledMymePreviewContainer
          $isClickable
          size='default'
          $previewImage={props.myme.previewImageFilePath}
          onClick={() => navigate(mymeDetailsPageRoute(props.myme.id))}
        >
          <MymePreviewHeader category={props.myme.category} />
          <MymePreviewDetails {...props.myme} />
          <MymeBottomPanelContainer {...props.myme} />
        </StyledMymePreviewContainer>
      );
    }
    case 'LIBRARY': {
      return (
        <FlexColumn mb={8}>
          <StyledMymePreviewContainer
            $isClickable
            size='default'
            onClick={() => navigate(mymeDetailsPageRoute(props.myme.id))}
            $previewImage={props.myme.previewImageFilePath}
          >
            <MymePreviewHeader category={props.myme.category} options={libraryOptions} />
            <MymePreviewDetails {...props.myme} />
            <MymeBottomPanelContainer {...props.myme} price={undefined} />
          </StyledMymePreviewContainer>
          <MymeLinearProgress value={props.myme.progress} />
        </FlexColumn>
      );
    }
    case 'DETAILS': {
      return (
        <StyledMymePreviewContainer $isClickable={false} size='large' $previewImage={props.myme.previewImageFilePath}>
          <div />
          <div />
          <MymeBottomPanelContainer {...props.myme} />
        </StyledMymePreviewContainer>
      );
    }
    case 'DETAILS-MOBILE': {
      return (
        <StyledMymePreviewContainer
          size={matchesMobile ? 'default' : 'large'}
          $isClickable={false}
          $previewImage={props.myme.previewImageFilePath}
        >
          <MymePreviewHeader options={detailsOptions} />
          <MymePreviewDetailsMobile {...props.myme} />
          <MymeBottomPanelContainer {...props.myme} />
          {reportMymeDialog.isOpen && (
            <ReportMymeDialog mymeId={props.myme.id} onClose={reportMymeDialog.closeDialog} />
          )}
          {embedMymeDialog.isOpen && (
            <EmbedMymeDialog mymeId={props.myme.id} myme={props.myme} onClose={embedMymeDialog.closeDialog} />
          )}
        </StyledMymePreviewContainer>
      );
    }

    default: {
      return null;
    }
  }
};
